import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Building2, MapPin, Phone, Mail, Globe, Plus, Check, 
  Loader2, ChevronDown, Facebook, Instagram, Shield, Clock, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '@/utils/api';

const CATEGORIES = [
  "Fryzjer", "Dentysta", "Mechanik samochodowy", "Kosmetyczka", "Hydraulik", "Elektryk",
  "Prawnik", "Psycholog", "Weterynarz", "Fizjoterapeuta", "Restauracja", "Dietetyk",
  "Fotograf", "Trener personalny", "Groomer", "Budowlaniec", "Firma sprzątająca",
  "Księgowa", "Tatuażysta", "Serwis komputerowy", "Salon masażu", "Kwiaciarnia",
  "Ogrodnik", "Szkoła jazdy", "Architekt", "Notariusz", "Nieruchomości", "Ubezpieczenia",
  "Fotowoltaika", "Klimatyzacja", "Taxi", "Przeprowadzki", "Wulkanizacja", "Pomoc drogowa",
  "Szkoła językowa", "Siłownia", "Cukiernia", "Sala weselna", "DJ i wodzirej",
  "Drukarnia", "Agencja reklamowa", "Medycyna estetyczna", "Stolarz", "Wypożyczalnia aut",
  "Brukarz", "Okna i drzwi", "Ogrodzenia", "Inna",
];

function SuccessScreen({ editUrl }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-lg mx-auto text-center py-12">
      <div className="w-20 h-20 bg-emerald-500/20 border-2 border-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
        <Check className="w-10 h-10 text-emerald-400" />
      </div>
      <h2 className="text-3xl font-black text-white mb-3" data-testid="success-title">Firma dodana do katalogu!</h2>
      <p className="text-gray-400 mb-6 text-lg">Twoja firma jest już widoczna w katalogu ApisAI.</p>

      {editUrl && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-8 text-left">
          <p className="text-amber-400 font-semibold text-sm mb-2">Zapisz ten link — do edycji i usuwania wpisu:</p>
          <div className="flex items-center gap-2">
            <input
              type="text" readOnly value={`${window.location.origin}${editUrl}`}
              className="flex-1 px-3 py-2 rounded-lg bg-black/30 border border-amber-500/20 text-white text-xs font-mono"
              onClick={e => { e.target.select(); navigator.clipboard.writeText(e.target.value); }}
              data-testid="edit-link-input"
            />
            <button
              onClick={() => { navigator.clipboard.writeText(`${window.location.origin}${editUrl}`); }}
              className="px-3 py-2 rounded-lg bg-amber-500 text-black text-xs font-bold hover:bg-amber-400"
              data-testid="edit-link-copy"
            >
              Kopiuj
            </button>
          </div>
          <p className="text-gray-500 text-xs mt-2">Kliknij w link aby edytować lub usunąć wpis.</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/katalog/uslugi">
          <Button className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-8 text-base">Zobacz katalog</Button>
        </Link>
        <Link to="/">
          <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-white/5 rounded-full px-8 text-base">Strona główna</Button>
        </Link>
      </div>
    </motion.div>
  );
}

export default function AddBusinessPage() {
  const [submitted, setSubmitted] = useState(false);
  const [editUrl, setEditUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showExtra, setShowExtra] = useState(false);
  const [form, setForm] = useState({
    name: '', category: '', city: '', phone: '', email: '',
    address: '', website: '', facebook: '', instagram: '', description: '', price_range: '', opening_hours: '',
  });

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Wpisz nazwę firmy'); return; }
    if (!form.category) { toast.error('Wybierz kategorię'); return; }
    if (!form.city.trim()) { toast.error('Wpisz miasto'); return; }
    if (!form.phone.trim() && !form.email.trim()) { toast.error('Podaj telefon lub email'); return; }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/business-submit`, form);
      setEditUrl(res.data?.edit_url || null);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd — spróbuj ponownie');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Dodaj firmę za darmo do katalogu | ApisAI</title>
        <meta name="description" content="Dodaj swoją firmę do katalogu ApisAI za darmo. Bez rejestracji. Dotrzesz do klientów szukających usług w Twoim mieście." />
        <link rel="canonical" href="https://apisai.pl/dodaj-firme" />
      </Helmet>

      <div className="min-h-screen bg-[#0B0F1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {/* Nav */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0F1A]/80 backdrop-blur-xl border-b border-white/5">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.svg" alt="ApisAI" className="w-9 h-9" />
              <span className="font-semibold text-lg text-white">ApisAI</span>
            </Link>
            <Link to="/katalog/uslugi" className="text-gray-400 hover:text-white text-sm">Katalog</Link>
          </div>
        </nav>

        <div className="pt-28 pb-20 px-4">
          <div className="max-w-lg mx-auto">
            {submitted ? <SuccessScreen editUrl={editUrl} /> : (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                {/* Header */}
                <div className="text-center mb-8">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium mb-4">
                    Darmowe zgłoszenie
                  </span>
                  <h1 className="text-3xl sm:text-4xl font-black text-white mb-2" data-testid="add-business-title">Dodaj firmę</h1>
                  <p className="text-gray-400">Pojawi się w katalogu od razu. Bez rejestracji.</p>
                </div>

                {/* Form */}
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-400 mb-1.5 block">Nazwa firmy *</label>
                    <input value={form.name} onChange={e => update('name', e.target.value)} placeholder="np. Salon Fryzjerski Anna"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 text-sm" data-testid="add-biz-name" />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-400 mb-1.5 block">Kategoria *</label>
                    <select value={form.category} onChange={e => update('category', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-emerald-500/50 text-sm appearance-none" data-testid="add-biz-category">
                      <option value="" className="bg-[#0B0F1A]">Wybierz kategorię...</option>
                      {CATEGORIES.map(c => <option key={c} value={c} className="bg-[#0B0F1A]">{c}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-400 mb-1.5 block">Miasto *</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <input value={form.city} onChange={e => update('city', e.target.value)} placeholder="np. Warszawa"
                        className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 text-sm" data-testid="add-biz-city" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-400 mb-1.5 block">Telefon *</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+48 123 456 789"
                        className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 text-sm" data-testid="add-biz-phone" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-gray-400 mb-1.5 block">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                      <input value={form.email} onChange={e => update('email', e.target.value)} placeholder="kontakt@firma.pl" type="email"
                        className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 text-sm" data-testid="add-biz-email" />
                    </div>
                  </div>

                  <p className="text-gray-500 text-xs">* Telefon lub email jest wymagany</p>

                  {/* Extra fields toggle */}
                  <button onClick={() => setShowExtra(!showExtra)} className="flex items-center gap-2 text-gray-400 text-sm hover:text-white transition-colors" data-testid="show-extra-fields">
                    <ChevronDown className={`w-4 h-4 transition-transform ${showExtra ? 'rotate-180' : ''}`} />
                    Pola dodatkowe (opis, www, social media)
                  </button>

                  {showExtra && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-4">
                      <div>
                        <label className="text-xs font-medium text-gray-400 mb-1.5 block">Adres</label>
                        <input value={form.address} onChange={e => update('address', e.target.value)} placeholder="ul. Przykładowa 10"
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 text-sm" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-400 mb-1.5 block">Opis firmy</label>
                        <textarea value={form.description} onChange={e => update('description', e.target.value)} rows={3}
                          placeholder="Czym się zajmujesz? Jakie usługi oferujesz?"
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 text-sm resize-none" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-400 mb-1.5 block">Strona www</label>
                          <input value={form.website} onChange={e => update('website', e.target.value)} placeholder="https://..."
                            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 text-sm" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-400 mb-1.5 block">Facebook</label>
                          <input value={form.facebook} onChange={e => update('facebook', e.target.value)} placeholder="https://fb.com/..."
                            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 text-sm" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-400 mb-1.5 block">Zakres cen</label>
                          <input value={form.price_range} onChange={e => update('price_range', e.target.value)} placeholder="np. 50-200 zł"
                            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 text-sm" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-400 mb-1.5 block">Godziny otwarcia</label>
                          <input value={form.opening_hours} onChange={e => update('opening_hours', e.target.value)} placeholder="Pon-Pt 9-17"
                            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 text-sm" />
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Submit */}
                  <Button onClick={handleSubmit} disabled={loading}
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl py-6 text-base font-semibold mt-2"
                    data-testid="add-biz-submit"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Plus className="w-5 h-5 mr-2" />}
                    Dodaj firmę
                  </Button>
                </div>

                {/* Trust badges */}
                <div className="flex items-center justify-center gap-6 mt-8 text-gray-500 text-xs">
                  <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Dodanie zajmuje 30 sekund</span>
                  <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-emerald-400" /> Darmowe</span>
                  <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Bez rejestracji</span>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
